/**
 * LocalPFormDialog — Create/Edit/Copy Local Purchase contract.
 * Sectioned form matching Access frmPurchaseQryLocalPAdd layout.
 * Field order per DocConvert_PurchaseLocalP §5.2 — no tabs.
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import type { SxProps, Theme } from '@mui/material/styles'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'

import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useListCertificationsQuery,
  useListDeliveryPlacesQuery,
  useListDeliveryTermsQuery,
  useListFixationCallsQuery,
  useListFuturesMonthsQuery,
  useListLicensesQuery,
  useListMeOrIbQuery,
  useListPackagesQuery,
  useListPriceUnitsQuery,
  useListQualitiesQuery,
  useListTradersQuery,
  useLookupVendorsQuery,
} from '@/store/api/lookupsApi'
import {
  useCreateLocalPMutation,
  useLazyCheckLocalPContractNoExistsQuery,
  useLazySuggestLocalPContractNoQuery,
  useUpdateLocalPMutation,
} from '@/store/api/localPApi'
import type { LocalPCreateIn, LocalPDetail } from '@/types/api'
import { FORM_GRID_3COL, HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'
import { getErrorMessage } from '@/utils/error'

import { EMPTY_LOCALP, type LocalPFormValues } from './localp-form-tabs/types'

export interface LocalPFormDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'copy'
  initialValue?: LocalPDetail | null
  copySource?: LocalPDetail | null
  onClose: () => void
  onSaved?: (contractNo: string) => void
}

// ── Validation schema ──────────────────────────────────────────────────────
const schema: yup.ObjectSchema<LocalPFormValues> = yup.object({
  contract_no: yup.string().trim().required('Please type P contract!')
    .test('p-prefix', "Please type Local P in the format 'P-*' !", (v) => !v || v.startsWith('P-')),
  contract_date: yup.string().required('Please type P contract date!'),
  cp_code: yup.string().trim().required('Please type P contract!'),
  seller_ref: yup.string().trim().default('').max(50),
  quality_code: yup.string().trim().default(''),
  quality_des_contract: yup.string().trim().default(''),
  certification: yup.string().trim().required('Please choose Certification!'),
  me_or_ib: yup.mixed<'ME' | 'IB' | ''>().oneOf(['ME', 'IB', ''] as const).default(''),
  license_cert_no: yup.string().trim().default(''),
  si: yup.mixed<number | ''>().default(''),
  sd: yup.mixed<number | ''>().default(''),
  quantity_kg: yup.mixed<number | ''>()
    .test('required', 'Please type quantity (kg)!', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Please type quantity (kg)!', (v) => typeof v === 'number' && v > 0)
    .required('Please type quantity (kg)!'),
  no_of_bag: yup.mixed<number | ''>()
    .test('required', 'Please type No of bags!', (v) => v !== '' && v !== undefined && v !== null)
    .required('Please type No of bags!'),
  package: yup.string().trim().required('Please choose package!'),

  contract_type: yup.mixed<'Outright' | 'Differential'>()
    .oneOf(['Outright', 'Differential'] as const)
    .required('Please choose contract type!'),
  outright_price: yup.mixed<number | ''>().when('contract_type', {
    is: 'Outright',
    then: (s) =>
      s.test('required', 'Please type Outright Price!', (v) => v !== '' && v !== undefined && v !== null)
       .test('positive', 'Please type Outright Price!', (v) => typeof v === 'number' && v > 0)
       .test('not-both-prices', 'Please type only Outright or PTBF price!', function (value) {
         const ptbfDiffs = (this.parent as LocalPFormValues).ptbf_diffs
         return !(value !== '' && value !== undefined && value !== null && ptbfDiffs !== '' && ptbfDiffs !== undefined && ptbfDiffs !== null)
       }),
    otherwise: (s) => s.default(''),
  }),
  ptbf_diffs: yup.mixed<number | ''>().when('contract_type', {
    is: 'Differential',
    then: (s) =>
      s.test('required', 'Please type PTBF price!', (v) => v !== '' && v !== undefined && v !== null)
        .test('not-both-prices', 'Please type only Outright or PTBF price!', function (value) {
          const outright = (this.parent as LocalPFormValues).outright_price
          return !(value !== '' && value !== undefined && value !== null && outright !== '' && outright !== undefined && outright !== null)
        }),
    otherwise: (s) => s.default(''),
  }),
  price_unit: yup.string().trim().required('Please choose Price unit!'),
  applicable_vat: yup.boolean().default(false),
  vat_rate: yup.mixed<number | ''>().when('applicable_vat', {
    is: true,
    then: (s) => s.test('required', 'Please input VAT Rate!', (v) => v !== '' && v !== undefined && v !== null),
    otherwise: (s) => s.test('empty-when-not-applicable', 'Please do not input VAT Rate!', (v) => v === '' || v === undefined || v === null),
  }),
  fx: yup.mixed<number | ''>().default(''),
  fixed_fx: yup.mixed<number | ''>().default(''),
  futures_month: yup.string().trim().required('Please choose Future month!'),
  fixation_call: yup.string().trim().when('contract_type', {
    is: 'Differential',
    then: (s) => s.required('Please choose Fixation Call !'),
    otherwise: (s) => s.default(''),
  }),

  latest_delivery: yup.string().trim()
    .required('Please choose Latest Delivery!')
    .test('after-contract-date', 'Latest Delivery must be >= Contract Date', function (value) {
      const cd = this.parent.contract_date as string
      if (!value || !cd) return true
      return value >= cd
    }),
  delivery_term: yup.string().trim().required('Please choose delivery term!'),
  delivery_place_code: yup.string().trim().required('Please choose delivery place!'),
  logistic_cost: yup.mixed<number | ''>().default(''),
  trader_in_charge: yup.string().trim().required('Please choose Trader In Charge!'),

  provi_market_rate: yup.mixed<number | ''>()
    .test('nonzero', 'Please input Provisional on Market Price!', (v) => v !== '' && v !== 0 && v !== undefined && v !== null)
    .required('Please input Provisional on Market Price!'),
  stoploss_rate: yup.mixed<number | ''>()
    .test('nonzero', 'Please input Stoploss rate!', (v) => v !== '' && v !== 0 && v !== undefined && v !== null)
    .required('Please input Stoploss rate!'),
  margin_value_per: yup.mixed<number | ''>().default(''),
  margin_limit_per: yup.mixed<number | ''>().default(''),
  time_limit: yup.mixed<number | ''>().default(''),
  margin_time_limit: yup.string().trim().default(''),
  margin_bonus_per: yup.mixed<number | ''>().default(''),
  margin_interest_month: yup.mixed<number | ''>().default(''),
  deposit_repayment_date: yup.string().default(''),

  is_intermediary: yup.boolean().default(false),
  shipper_code: yup.string().trim().when('is_intermediary', {
    is: true,
    then: (s) => s.required('Please choose Shipper Code!'),
    otherwise: (s) => s.default(''),
  }),
  contract_shipper_intermediary: yup.string().trim().default(''),
  outright_shipper_intermediary: yup.mixed<number | ''>().default(''),
  ptbfdiffs_shipper_intermediary: yup.mixed<number | ''>().default(''),

  is_issued: yup.boolean().default(false),
  is_sent_mail: yup.boolean().default(false),
  is_received_mail: yup.boolean().default(false),
  is_sent_paper: yup.boolean().default(false),
  is_received_paper: yup.boolean().default(false),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<LocalPFormValues>

// ── Payload helpers ────────────────────────────────────────────────────────
function toApiPayload(v: LocalPFormValues): LocalPCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  return {
    contract_no: str(v.contract_no),
    contract_date: v.contract_date,
    cp_code: v.cp_code.trim(),
    seller_ref: str(v.seller_ref),
    quality_code: str(v.quality_code),
    quality_des_contract: str(v.quality_des_contract),
    certification: str(v.certification),
    me_or_ib: v.me_or_ib === '' ? null : v.me_or_ib,
    license_cert_no: str(v.license_cert_no),
    si: num(v.si),
    sd: num(v.sd),
    applicable_vat: v.applicable_vat,
    quantity_kg: typeof v.quantity_kg === 'number' ? v.quantity_kg : 0,
    no_of_bag: num(v.no_of_bag),
    package: str(v.package),
    contract_type: v.contract_type,
    outright_price: num(v.outright_price),
    ptbf_diffs: num(v.ptbf_diffs),
    price_unit: str(v.price_unit),
    vat_rate: v.applicable_vat ? num(v.vat_rate) : null,
    fx: num(v.fx),
    fixed_fx: num(v.fixed_fx),
    futures_month: str(v.futures_month),
    fixation_call: str(v.fixation_call),
    latest_delivery: str(v.latest_delivery),
    delivery_term: str(v.delivery_term),
    delivery_place_code: str(v.delivery_place_code),
    logistic_cost: num(v.logistic_cost),
    trader_in_charge: str(v.trader_in_charge),
    provi_market_rate: num(v.provi_market_rate),
    stoploss_rate: num(v.stoploss_rate),
    margin_value_per: num(v.margin_value_per),
    margin_limit_per: num(v.margin_limit_per),
    margin_time_limit: str(v.margin_time_limit),
    margin_bonus_per: num(v.margin_bonus_per),
    margin_interest_month: num(v.margin_interest_month),
    deposit_repayment_date: str(v.deposit_repayment_date),
    is_intermediary: v.is_intermediary,
    shipper_code: str(v.shipper_code),
    contract_shipper_intermediary: str(v.contract_shipper_intermediary),
    outright_shipper_intermediary: num(v.outright_shipper_intermediary),
    ptbfdiffs_shipper_intermediary: num(v.ptbfdiffs_shipper_intermediary),
    is_issued: v.is_issued,
    is_sent_mail: v.is_sent_mail,
    is_received_mail: v.is_received_mail,
    is_sent_paper: v.is_sent_paper,
    is_received_paper: v.is_received_paper,
    note: str(v.note),
  }
}

function fromDetail(d: LocalPDetail): LocalPFormValues {
  const n = (x: number | null | undefined): number | '' => (x === null || x === undefined ? '' : x)
  const s = (x: string | null | undefined): string => x ?? ''
  return {
    contract_no: s(d.contract_no),
    contract_date: s(d.contract_date),
    cp_code: s(d.supplier_code),
    seller_ref: s(d.seller_ref),
    quality_code: s(d.quality_code),
    quality_des_contract: s(d.quality_des_contract),
    certification: s(d.certification),
    // `local_p.me_or_ib` là CHAR(50) → padded space → MUI Select trượt match
    // "ME"/"IB" + yup `oneOf(['ME','IB',''])` reject. Trim + chuẩn hoá enum.
    me_or_ib: ((): 'ME' | 'IB' | '' => {
      const raw = (d.me_or_ib ?? '').trim()
      return raw === 'ME' || raw === 'IB' ? raw : ''
    })(),
    license_cert_no: s(d.license_cert_no),
    si: n(d.si),
    sd: n(d.sd),
    applicable_vat: d.vat_rate !== null && d.vat_rate !== undefined,
    quantity_kg: n(d.quantity_kg),
    no_of_bag: n(d.no_of_bag),
    package: s(d.package),
    contract_type: (d.contract_type as 'Outright' | 'Differential') ?? 'Outright',
    outright_price: n(d.outright_price),
    ptbf_diffs: n(d.ptbf_diffs),
    price_unit: s(d.price_unit),
    vat_rate: n(d.vat_rate),
    fx: n(d.fx),
    fixed_fx: n(d.fixed_fx),
    futures_month: s(d.futures_month),
    fixation_call: s(d.fixation_call),
    latest_delivery: s(d.latest_delivery),
    delivery_term: s(d.delivery_term),
    delivery_place_code: s(d.delivery_place_code),
    logistic_cost: n(d.logistic_cost),
    trader_in_charge: s(d.trader_in_charge),
    provi_market_rate: n(d.provi_market_rate),
    stoploss_rate: n(d.stoploss_rate),
    margin_value_per: n(d.margin_value_per),
    margin_limit_per: n(d.margin_limit_per),
    time_limit: n(d.time_limit),
    margin_time_limit: s(d.margin_time_limit),
    margin_bonus_per: n(d.margin_bonus_per),
    margin_interest_month: n(d.margin_interest_month),
    deposit_repayment_date: s(d.deposit_repayment_date),
    is_intermediary: !!d.is_intermediary,
    shipper_code: s(d.shipper_code),
    contract_shipper_intermediary: s(d.contract_shipper_intermediary),
    outright_shipper_intermediary: n(d.outright_shipper_intermediary),
    ptbfdiffs_shipper_intermediary: n(d.ptbfdiffs_shipper_intermediary),
    is_issued: !!d.is_issued,
    is_sent_mail: !!d.is_sent_mail,
    is_received_mail: !!d.is_received_mail,
    is_sent_paper: !!d.is_sent_paper,
    is_received_paper: !!d.is_received_paper,
    note: s(d.note),
  }
}

function fromDetailCopy(d: LocalPDetail, suggestedContractNo = ''): LocalPFormValues {
  const n = (x: number | null | undefined): number | '' => (x === null || x === undefined ? '' : x)
  const s = (x: string | null | undefined): string => x ?? ''
  return {
    ...EMPTY_LOCALP,
    cp_code: s(d.supplier_code),
    quality_code: s(d.quality_code),
    seller_ref: s(d.seller_ref),
    certification: s(d.certification),
    me_or_ib: ((): 'ME' | 'IB' | '' => {
      const raw = (d.me_or_ib ?? '').trim()
      return raw === 'ME' || raw === 'IB' ? raw : ''
    })(),
    license_cert_no: s(d.license_cert_no),
    si: n(d.si),
    sd: n(d.sd),
    package: s(d.package),
    contract_type: (d.contract_type as 'Outright' | 'Differential') ?? 'Outright',
    price_unit: s(d.price_unit),
    futures_month: s(d.futures_month),
    fixation_call: s(d.fixation_call),
    logistic_cost: n(d.logistic_cost),
    delivery_term: s(d.delivery_term),
    delivery_place_code: s(d.delivery_place_code),
    note: s(d.note),
    fixed_fx: n(d.fixed_fx),
    provi_market_rate: n(d.provi_market_rate),
    stoploss_rate: n(d.stoploss_rate),
    shipper_code: s(d.shipper_code),
    contract_shipper_intermediary: s(d.contract_shipper_intermediary),
    trader_in_charge: s(d.trader_in_charge),
    deposit_repayment_date: s(d.deposit_repayment_date),
    is_intermediary: false,
    contract_no: suggestedContractNo,
    contract_date: dayjs().format('YYYY-MM-DD'),
    quantity_kg: '',
    no_of_bag: '',
    outright_price: '',
    ptbf_diffs: '',
    fx: '',
    applicable_vat: false,
    vat_rate: '',
    margin_value_per: '',
    margin_limit_per: '',
    time_limit: '',
    margin_time_limit: '',
    margin_bonus_per: '',
    margin_interest_month: '',
    outright_shipper_intermediary: '',
    ptbfdiffs_shipper_intermediary: '',
    is_issued: false,
    is_sent_mail: false,
    is_received_mail: false,
    is_sent_paper: false,
    is_received_paper: false,
  }
}

// ── Component ──────────────────────────────────────────────────────────────
export function LocalPFormDialog({
  open,
  mode,
  initialValue,
  copySource,
  onClose,
  onSaved,
}: LocalPFormDialogProps) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const isEdit = mode === 'edit'

  const [createLocalP, { isLoading: creating }] = useCreateLocalPMutation()
  const [updateLocalP, { isLoading: updating }] = useUpdateLocalPMutation()
  const loading = creating || updating

  // Pattern Futures Order (skill access-suggest): CHỈ dùng lazy hook —
  // tránh cache shared giữa regular query và lazy gây race.
  const [triggerCheckPContract] = useLazyCheckLocalPContractNoExistsQuery()
  const [triggerSuggestPContract, suggestPContractState] =
    useLazySuggestLocalPContractNoQuery()
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<LocalPFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY_LOCALP,
  })

  // Watched values for derived display + conditional logic
  const cpCode           = useWatch({ control, name: 'cp_code' })
  const packageCode      = useWatch({ control, name: 'package' })
  const deliveryPlaceCode = useWatch({ control, name: 'delivery_place_code' })
  const shipperCode      = useWatch({ control, name: 'shipper_code' })
  const applicableVat    = useWatch({ control, name: 'applicable_vat' })
  const contractType     = useWatch({ control, name: 'contract_type' })
  const currentQty       = useWatch({ control, name: 'quantity_kg' })
  const si               = useWatch({ control, name: 'si' })
  const sd               = useWatch({ control, name: 'sd' })

  // Lookup data
  const { data: vendors       = [] } = useLookupVendorsQuery({ q: '', limit: 1000 })
  const { data: qualities     = [] } = useListQualitiesQuery({ local_only: false })
  const { data: certifications = [] } = useListCertificationsQuery()
  const { data: meOrIbOpts    = [] } = useListMeOrIbQuery()
  const { data: packages      = [] } = useListPackagesQuery()
  const { data: licenses      = [] } = useListLicensesQuery({ only_valid: true })
  const { data: priceUnits    = [] } = useListPriceUnitsQuery()
  const { data: futuresMonths = [] } = useListFuturesMonthsQuery()
  const { data: fixationCalls = [] } = useListFixationCallsQuery()
  const { data: deliveryTerms = [] } = useListDeliveryTermsQuery()
  const { data: deliveryPlaces = [] } = useListDeliveryPlacesQuery()
  const { data: traders       = [] } = useListTradersQuery()

  // Autofill display-only values derived from lookup selections
  const supplierName     = useMemo(() => vendors.find((v) => v.code === cpCode)?.name ?? '', [vendors, cpCode])
  const packageName      = useMemo(() => packages.find((p) => p.value === packageCode)?.label ?? '', [packages, packageCode])
  const deliveryPlaceName = useMemo(() => deliveryPlaces.find((p) => p.value === deliveryPlaceCode)?.label ?? '', [deliveryPlaces, deliveryPlaceCode])
  const shipperName      = useMemo(() => vendors.find((v) => v.code === shipperCode)?.name ?? '', [vendors, shipperCode])

  // Hydrate khi dialog mở / đổi record. CHÚ Ý (skill anti-pattern #4):
  // KHÔNG đưa `suggestedContractNo` vào deps — khi user blur trigger
  // refetch suggest, cache update → effect re-fire → reset toàn bộ form
  // → mất data user đang nhập. Áp dụng contract_no qua effect riêng (dưới)
  // chỉ khi field còn rỗng.
  useEffect(() => {
    if (!open) return
    if (mode === 'copy' && copySource) {
      reset(fromDetailCopy(copySource, ''))
    } else if (isEdit && initialValue) {
      reset(fromDetail(initialValue))
    } else {
      reset({
        ...EMPTY_LOCALP,
        contract_no: '',
        contract_date: dayjs().format('YYYY-MM-DD'),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, isEdit, initialValue, copySource, reset])

  // Pattern Futures Order: lazy trigger suggest sau khi dialog mở (mode!=edit).
  // Force refetch để lấy mã mới nhất. Apply vào field nếu còn rỗng.
  useEffect(() => {
    if (!open || isEdit) return
    let cancelled = false
    void triggerSuggestPContract(undefined, false)
      .unwrap()
      .then((res) => {
        if (cancelled) return
        const newVal = (res.contractNo || '').trim()
        if (!newVal) return
        const cur = (getValues('contract_no') ?? '').trim()
        if (cur) return
        setValue('contract_no', newVal, { shouldDirty: false })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [open, isEdit, triggerSuggestPContract, getValues, setValue])

  // Reset pre-save check state khi dialog mở (skill access-suggest).
  useEffect(() => {
    if (!open) return
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
  }, [open, mode, isEdit, initialValue?.contract_no])

  // Auto-dismiss alert khi user sửa contract_no khác mã suggest.
  const watchedContractNo = useWatch({ control, name: 'contract_no' })
  useEffect(() => {
    if (regenNotice && (watchedContractNo ?? '').trim() !== regenNotice.to) {
      setRegenNotice(null)
    }
  }, [watchedContractNo, regenNotice])

  const handleContractNoBlur = async () => {
    if (isEdit) return
    const val = (watchedContractNo ?? '').trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheckPContract({ contractNo: val }).unwrap()
      if (!check.exists) return
      // preferCacheValue=false → force network refetch (skill anti-pattern #7).
      const next = await triggerSuggestPContract(undefined, false).unwrap()
      const newVal = (next.contractNo || '').trim()
      if (newVal && newVal !== val) {
        setValue('contract_no', newVal, { shouldDirty: true })
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE sẽ raise lúc Save nếu thật sự trùng — không poison UX.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const handleLicenseChange = (onChange: (v: string) => void, value: string) => {
    onChange(value)
    const lic = licenses.find((l) => l.license_cert_no === value)
    setValue('si', lic?.si ?? '')
    setValue('sd', lic?.sd ?? '')
  }

  const packageNetWeight = (packageValue: string): number => {
    const option = packages.find((p) => p.value === packageValue) as { nw_per_bag_kg?: number | string | null } | undefined
    const parsed = Number(option?.nw_per_bag_kg)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 60
  }

  const handleQuantityChange = (onChange: (v: number | '') => void, raw: string) => {
    const parsed = raw === '' ? '' : Number(raw)
    onChange(parsed)
    if (!isEdit && typeof parsed === 'number' && parsed > 0) {
      setValue('no_of_bag', Math.round(parsed / packageNetWeight(packageCode)))
    }
  }

  const handlePackageChange = (onChange: (v: string) => void, value: string) => {
    onChange(value)
    if (!isEdit && typeof currentQty === 'number' && currentQty > 0) {
      setValue('no_of_bag', Math.round(currentQty / packageNetWeight(value)))
    }
  }

  const onSubmit = handleSubmit(
    async (values) => {
      // Race guard (skill anti-pattern #5): blur + Save click cùng tick.
      if (precheckBusyRef.current) return
      const payload = toApiPayload(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateLocalP({ contractNo: initialValue.contract_no, body: payload }).unwrap()
          snack.success(`Contract ${initialValue.contract_no} updated successfully`)
          onSaved?.(res.data?.contract_no ?? initialValue.contract_no)
        } else {
          const res = await createLocalP(payload).unwrap()
          const newNo = res.data?.contract_no ?? ''
          snack.success(`Contract ${newNo} created successfully`)
          onSaved?.(newNo)
        }
        onClose()
      } catch (err) {
        const errMsg = getErrorMessage(err, isEdit ? 'Update failed' : 'Create contract failed')
        // Skill access-suggest anti #13: BE raise duplicate → tự suggest mã mới.
        // BE messages đã chuẩn hoá English ("already exists" / "duplicate").
        if (!isEdit && /exist|duplicate/i.test(errMsg)) {
          try {
            const next = await triggerSuggestPContract(undefined, false).unwrap()
            const newVal = (next.contractNo || '').trim()
            const oldVal = (values.contract_no ?? '').trim()
            if (newVal && newVal !== oldVal) {
              setValue('contract_no', newVal, { shouldDirty: true })
              setRegenNotice({ from: oldVal, to: newVal })
              return
            }
          } catch { /* fallthrough */ }
        }
        snack.error(errMsg)
      }
    },
    () => { snack.error('Please check the highlighted fields!') },
  )

  // Form 3-col responsive (xs 1-col / sm 2-col / md+ 3-col) — bám template
  // `FORM_GRID_3COL` ở `theme/responsive.ts`; gap dense 1 để giữ compact spec
  // LocalP (form rất nhiều field, default template gap 1.5 sẽ quá thưa).
  const GRID = { ...FORM_GRID_3COL, gap: 1, columnGap: 1, rowGap: 1 } as const
  const SPAN2 = { gridColumn: 'span 2' } as const
  const SPAN3 = { gridColumn: 'span 3' } as const
  const RO = { readOnly: true } as const
  const SM = 'small' as const
  // Skill `/convert-form` readonly bg standard: neutral gray `#f1f3f5` thay
  // `action.hover` overlay (trên theme TLC cream → phớt hồng); fontWeight 600
  // + text.secondary để phân biệt rõ readonly vs editable. Áp dụng cho field
  // `Enabled = NotDefault` trong Access (License SI/SD, Packaging, Delivery
  // Place Name, Shipper Name, …).
  const readonlyFieldSx = {
    '& .MuiInputBase-root': {
      bgcolor: (theme: { palette: { mode: string } }) =>
        theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
    },
    '& .MuiInputBase-input': {
      color: 'text.secondary',
      fontWeight: 600,
    },
  } as const
  const sectionSx: SxProps<Theme> = {
    ...SPAN3,
    mt: 0.5,
    color: 'error.main',
    fontWeight: 700,
    fontSize: '0.8125rem',
  }

  const dialogTitle =
    mode === 'edit' ? 'Purchase - Local P (Edit)'
    : mode === 'copy' ? 'Purchase - Local P (Copy)'
    : 'Purchase - Local P (New)'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(900px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          component="span"
          noWrap
          title={dialogTitle}
          sx={{
            flex: '1 1 auto',
            fontWeight: 700,
            fontSize: '1rem',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Purchase - </Box>
          {mode === 'edit' ? 'Local P (Edit)' : mode === 'copy' ? 'Local P (Copy)' : 'Local P (New)'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void onSubmit()}
            disabled={loading || !canEdit}
            sx={{
              py: 0.25,
              px: 1,
              minHeight: 28,
              fontSize: '0.75rem',
              lineHeight: 1.2,
              textTransform: 'none',
            }}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={loading}
            sx={{
              py: 0.25,
              px: 1,
              minHeight: 28,
              fontSize: '0.75rem',
              lineHeight: 1.2,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          '& .MuiInputBase-root': {
            minHeight: 32,
            fontSize: '0.8125rem',
          },
          '& .MuiInputBase-input': {
            py: 0.5,
            fontSize: '0.8125rem',
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.8125rem',
            fontWeight: 500,
          },
          '& .MuiInputLabel-shrink': {
            fontWeight: 600,
          },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.8125rem',
            fontWeight: 500,
          },
          '& .MuiCheckbox-root': {
            p: 0.5,
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1rem',
          },
          '& .MuiSelect-select': {
            fontSize: '0.8125rem',
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.6875rem',
            mt: 0.25,
          },
        }}
      >
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
            Contract No. <b>{regenNotice.from}</b> was just created — suggested
            new code <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Box sx={{ ...GRID, maxWidth: 980, mx: 'auto', pt: 0.5, pb: 1.5 }}>

          {/* R1: Contract No. | Supplier Code (span 2) */}
          <Controller control={control} name="contract_no" render={({ field }) => (
            <TextField {...field} label="Contract No." size={SM}
              disabled={isEdit} placeholder={isEdit ? '' : 'Auto: P-XXXXX'}
              onBlur={(e) => { field.onBlur(); void handleContractNoBlur(); void e }}
              error={!!errors.contract_no}
              helperText={
                errors.contract_no?.message ??
                (precheckBusy
                  ? 'Verifying contract code…'
                  : suggestPContractState.isFetching
                    ? 'Suggesting next code…'
                    : undefined)
              } />
          )} />
          <Controller control={control} name="cp_code" render={({ field }) => {
            const sel = vendors.find((v) => v.code === field.value)
              ?? (field.value ? { code: field.value, name: '', short_name: null, vat_code: null } : null)
            return (
              <Autocomplete size={SM} sx={SPAN2} options={vendors} value={sel}
                onChange={(_e, v) => field.onChange(v?.code ?? '')}
                getOptionLabel={(o) => o ? `${o.code} — ${o.name ?? ''}` : ''}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                renderInput={(params) => (
                  <TextField {...params} label="Supplier Code *"
                    error={!!errors.cp_code} helperText={errors.cp_code?.message} />
                )} />
            )
          }} />

          {/* R2: Contract Date | Supplier Name (readonly, span 2) */}
          <Controller control={control} name="contract_date" render={({ field }) => (
            <DatePicker label="Contract Date *" format="DD-MMM-YYYY"
              value={field.value ? dayjs(field.value) : null}
              onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: SM, error: !!errors.contract_date, helperText: errors.contract_date?.message } }} />
          )} />
          <TextField label="Supplier Name" size={SM} value={supplierName}
            inputProps={RO} sx={{ ...SPAN2, ...readonlyFieldSx }} />

          {/* R3: Quality Code | Seller's Ref (span 2) */}
          <Controller control={control} name="quality_code" render={({ field }) => {
            const sel = qualities.find((q) => q.code === field.value)
              ?? (field.value ? { code: field.value, description: '' } : null)
            return (
              <Autocomplete size={SM} options={qualities} value={sel}
                onChange={(_e, v) => field.onChange(v?.code ?? '')}
                getOptionLabel={(o) => o ? `${o.code} — ${o.description ?? ''}` : ''}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                renderInput={(params) => (
                  <TextField {...params} label="Quality Code"
                    error={!!errors.quality_code} helperText={errors.quality_code?.message} />
                )} />
            )
          }} />
          <Controller control={control} name="seller_ref" render={({ field }) => (
            <TextField {...field} label="Seller's Ref" size={SM} sx={SPAN2}
              error={!!errors.seller_ref} helperText={errors.seller_ref?.message} />
          )} />

          {/* R4: Quantity (Kg) | No of Bags | License Cert No. */}
          <Controller control={control} name="quantity_kg" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => handleQuantityChange(field.onChange, e.target.value)}
              label="Quantity (Kg) *" type="number" size={SM}
              error={!!errors.quantity_kg} helperText={errors.quantity_kg?.message} />
          )} />
          <Controller control={control} name="no_of_bag" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="No of Bags *" type="number" size={SM}
              error={!!errors.no_of_bag} helperText={errors.no_of_bag?.message} />
          )} />
          <Controller control={control} name="license_cert_no" render={({ field }) => {
            const sel = licenses.find((l) => l.license_cert_no === field.value)
              ?? (field.value ? { license_id: '', license_cert_no: field.value } : null)
            return (
              <Autocomplete size={SM} options={licenses} value={sel}
                onChange={(_e, v) => handleLicenseChange(field.onChange, v?.license_cert_no ?? '')}
                getOptionLabel={(o) => o ? o.license_cert_no : ''}
                isOptionEqualToValue={(a, b) => a.license_cert_no === b.license_cert_no}
                renderInput={(params) => (
                  <TextField {...params} label="License Cert No."
                    error={!!errors.license_cert_no} helperText={errors.license_cert_no?.message} />
                )} />
            )
          }} />

          {/* R5: Certification | ME or IB | SI (readonly) */}
          <Controller control={control} name="certification" render={({ field }) => {
            const sel = certifications.find((c) => c.certification === field.value)
              ?? (field.value ? { certification: field.value } : null)
            return (
              <Autocomplete size={SM} options={certifications} value={sel}
                onChange={(_e, v) => field.onChange(v?.certification ?? '')}
                getOptionLabel={(o) => o ? o.certification : ''}
                isOptionEqualToValue={(a, b) => a.certification === b.certification}
                renderInput={(params) => (
                  <TextField {...params} label="Certification *"
                    error={!!errors.certification} helperText={errors.certification?.message} />
                )} />
            )
          }} />
          <Controller control={control} name="me_or_ib" render={({ field }) => {
            const sel = meOrIbOpts.find((o) => o.value === field.value)
              ?? (field.value ? { value: field.value as 'ME' | 'IB', label: '' } : null)
            return (
              <Autocomplete size={SM} options={meOrIbOpts} value={sel}
                onChange={(_e, v) => field.onChange(v?.value ?? '')}
                getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <TextField {...params} label="ME or IB"
                    error={!!errors.me_or_ib} helperText={errors.me_or_ib?.message} />
                )} />
            )
          }} />
          <TextField label="License - SI (VND/KG)" size={SM} value={si === '' ? '' : si} inputProps={RO} sx={readonlyFieldSx} />

          {/* R6: Contract Type | Package Code | SD (readonly) */}
          <Controller control={control} name="contract_type" render={({ field }) => {
            const opts: { value: 'Outright' | 'Differential' }[] = [{ value: 'Outright' }, { value: 'Differential' }]
            const sel = opts.find((o) => o.value === field.value) ?? null
            return (
              <Autocomplete size={SM} options={opts} value={sel}
                onChange={(_e, v) => {
                  const next = v?.value
                  if (!next) return
                  field.onChange(next)
                  // Outright → khoá PTBF Diffs (clear giá trị); Differential → khoá Outright Price.
                  if (next === 'Outright') setValue('ptbf_diffs', '', { shouldValidate: true })
                  else setValue('outright_price', '', { shouldValidate: true })
                }}
                getOptionLabel={(o) => o ? o.value : ''}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <TextField {...params} label="Contract Type *"
                    error={!!errors.contract_type} helperText={errors.contract_type?.message} />
                )} />
            )
          }} />
          <Controller control={control} name="package" render={({ field }) => {
            const sel = packages.find((p) => p.value === field.value)
              ?? (field.value ? { value: field.value, label: '' } : null)
            return (
              <Autocomplete size={SM} options={packages} value={sel}
                onChange={(_e, v) => handlePackageChange(field.onChange, v?.value ?? '')}
                getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <TextField {...params} label="Package Code *"
                    error={!!errors.package} helperText={errors.package?.message} />
                )} />
            )
          }} />
          <TextField label="License - SD (VND/KG)" size={SM} value={sd === '' ? '' : sd} inputProps={RO} sx={readonlyFieldSx} />

          {/* R7: Price Unit | Packaging (readonly, span 2) */}
          <Controller control={control} name="price_unit" render={({ field }) => {
            const sel = priceUnits.find((u) => u.value === field.value)
              ?? (field.value ? { value: field.value, label: '' } : null)
            return (
              <Autocomplete size={SM} options={priceUnits} value={sel}
                onChange={(_e, v) => field.onChange(v?.value ?? '')}
                getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <TextField {...params} label="Price Unit *"
                    error={!!errors.price_unit} helperText={errors.price_unit?.message} />
                )} />
            )
          }} />
          <TextField label="Packaging" size={SM} value={packageName} inputProps={RO} sx={{ ...SPAN2, ...readonlyFieldSx }} />

          {/* R8: Outright Price | PTBF Diffs | (empty)
              Outright → chỉ enable Outright Price; Differential → chỉ enable PTBF Diffs. */}
          <Controller control={control} name="outright_price" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="Outright Price" type="number" size={SM}
              disabled={contractType === 'Differential'}
              error={!!errors.outright_price} helperText={errors.outright_price?.message} />
          )} />
          <Controller control={control} name="ptbf_diffs" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="PTBF Diffs" type="number" size={SM}
              disabled={contractType === 'Outright'}
              error={!!errors.ptbf_diffs} helperText={errors.ptbf_diffs?.message} />
          )} />
          <Box />

          {/* R9: Applicable VAT? | VAT Rate | (empty) */}
          <Controller control={control} name="applicable_vat" render={({ field }) => (
            <FormControlLabel sx={{ alignSelf: 'center' }}
              control={<Checkbox checked={!!field.value} onChange={(e) => {
                field.onChange(e.target.checked)
                if (!e.target.checked) setValue('vat_rate', '')
              }} size={SM} />}
              label="Applicable VAT?" />
          )} />
          <Controller control={control} name="vat_rate" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="VAT Rate" type="number" size={SM} disabled={!applicableVat}
              error={!!errors.vat_rate} helperText={errors.vat_rate?.message} />
          )} />
          <Box />

          {/* R10: FX | Futures Month | Fixation Call */}
          <Controller control={control} name="fx" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="FX" type="number" size={SM}
              error={!!errors.fx} helperText={errors.fx?.message} />
          )} />
          <Controller control={control} name="futures_month" render={({ field }) => {
            const sel = futuresMonths.find((m) => m.value === field.value)
              ?? (field.value ? { value: field.value, label: '' } : null)
            return (
              <Autocomplete size={SM} options={futuresMonths} value={sel}
                onChange={(_e, v) => field.onChange(v?.value ?? '')}
                getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <TextField {...params} label="Futures Month"
                    error={!!errors.futures_month} helperText={errors.futures_month?.message} />
                )} />
            )
          }} />
          <Controller control={control} name="fixation_call" render={({ field }) => {
            const sel = fixationCalls.find((c) => c.value === field.value)
              ?? (field.value ? { value: field.value, label: '' } : null)
            return (
              <Autocomplete size={SM} options={fixationCalls} value={sel}
                onChange={(_e, v) => field.onChange(v?.value ?? '')}
                getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <TextField {...params} label="Fixation Call"
                    error={!!errors.fixation_call} helperText={errors.fixation_call?.message} />
                )} />
            )
          }} />

          {/* R11: Latest Delivery | Delivery Term | Logistic Cost */}
          <Controller control={control} name="latest_delivery" render={({ field }) => (
            <DatePicker label="Latest Delivery *" format="DD-MMM-YYYY"
              value={field.value ? dayjs(field.value) : null}
              onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: SM, error: !!errors.latest_delivery, helperText: errors.latest_delivery?.message } }} />
          )} />
          <Controller control={control} name="delivery_term" render={({ field }) => {
            const sel = deliveryTerms.find((t) => t.value === field.value)
              ?? (field.value ? { value: field.value, label: '' } : null)
            return (
              <Autocomplete size={SM} options={deliveryTerms} value={sel}
                onChange={(_e, v) => field.onChange(v?.value ?? '')}
                getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <TextField {...params} label="Delivery Term *"
                    error={!!errors.delivery_term} helperText={errors.delivery_term?.message} />
                )} />
            )
          }} />
          <Controller control={control} name="logistic_cost" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="Logistic Cost" type="number" size={SM}
              error={!!errors.logistic_cost} helperText={errors.logistic_cost?.message} />
          )} />

          {/* R12: Delivery Place | Delivery Place Name (readonly, span 2) */}
          <Controller control={control} name="delivery_place_code" render={({ field }) => {
            const sel = deliveryPlaces.find((p) => p.value === field.value)
              ?? (field.value ? { value: field.value, label: '' } : null)
            return (
              <Autocomplete size={SM} options={deliveryPlaces} value={sel}
                onChange={(_e, v) => field.onChange(v?.value ?? '')}
                getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <TextField {...params} label="Delivery Place *"
                    error={!!errors.delivery_place_code} helperText={errors.delivery_place_code?.message} />
                )} />
            )
          }} />
          <TextField label="Delivery Place Name" size={SM} value={deliveryPlaceName} inputProps={RO} sx={{ ...SPAN2, ...readonlyFieldSx }} />

          {/* R13: Note (full width) */}
          <Controller control={control} name="note" render={({ field }) => (
            <TextField {...field} label="Note" size={SM} multiline minRows={2} sx={SPAN3}
              error={!!errors.note} helperText={errors.note?.message} />
          )} />

          {/* R13: Trader In Charge */}
          <Controller control={control} name="trader_in_charge" render={({ field }) => {
            const sel = traders.find((t) => t.username === field.value)
              ?? (field.value ? { username: field.value, full_name: '' } : null)
            return (
              <Autocomplete size={SM} options={traders} value={sel}
                onChange={(_e, v) => field.onChange(v?.username ?? '')}
                getOptionLabel={(o) => o ? `${o.username} — ${o.full_name ?? ''}` : ''}
                isOptionEqualToValue={(a, b) => a.username === b.username}
                renderInput={(params) => (
                  <TextField {...params} label="Trader In Charge *"
                    error={!!errors.trader_in_charge} helperText={errors.trader_in_charge?.message} />
                )} />
            )
          }} />
          <Box />
          <Box />

          <Typography sx={sectionSx}>Payment Information</Typography>

          {/* R15: Fixed FX | Provi. on Market Price | Stoploss Rate */}
          <Controller control={control} name="fixed_fx" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="Fixed FX (If have)" type="number" size={SM}
              error={!!errors.fixed_fx} helperText={errors.fixed_fx?.message} />
          )} />
          <Controller control={control} name="provi_market_rate" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="Provi. on Market Price *" type="number" size={SM}
              error={!!errors.provi_market_rate} helperText={errors.provi_market_rate?.message} />
          )} />
          <Controller control={control} name="stoploss_rate" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="Stoploss Rate *" type="number" size={SM}
              error={!!errors.stoploss_rate} helperText={errors.stoploss_rate?.message} />
          )} />

          <Typography sx={sectionSx}>Forward Purchase - Information</Typography>

          {/* R17: Deposit Value (%) | Deposit Limit (%) | (empty) */}
          <Controller control={control} name="margin_value_per" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="Deposit Value (%)" type="number" size={SM}
              error={!!errors.margin_value_per} helperText={errors.margin_value_per?.message} />
          )} />
          <Controller control={control} name="margin_limit_per" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="Deposit Limit (%)" type="number" size={SM}
              error={!!errors.margin_limit_per} helperText={errors.margin_limit_per?.message} />
          )} />
          <Box />

          {/* R18: Time limit | Deposit Bonus (%) | Interest (%/month) */}
          <Controller control={control} name="margin_time_limit" render={({ field }) => (
            <TextField {...field}
              label="Time limit" size={SM}
              error={!!errors.margin_time_limit} helperText={errors.margin_time_limit?.message} />
          )} />
          <Controller control={control} name="margin_bonus_per" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="Deposit Bonus (%)" type="number" size={SM}
              error={!!errors.margin_bonus_per} helperText={errors.margin_bonus_per?.message} />
          )} />
          <Controller control={control} name="margin_interest_month" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="Interest (%/month)" type="number" size={SM}
              error={!!errors.margin_interest_month} helperText={errors.margin_interest_month?.message} />
          )} />

          {/* R19: Deposit Repayment Date */}
          <Controller control={control} name="deposit_repayment_date" render={({ field }) => (
            <DatePicker label="Repayment Date" format="DD-MMM-YYYY"
              value={field.value ? dayjs(field.value) : null}
              onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: SM, error: !!errors.deposit_repayment_date, helperText: errors.deposit_repayment_date?.message } }} />
          )} />
          <Box sx={SPAN2} />

          {/* R19: Checkboxes — Intermediary + document tracking. 3-col sub-grid for alignment. */}
          <Box
            sx={{
              ...SPAN3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 1.25,
              py: 0.75,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
              columnGap: 1,
              rowGap: 0.25,
              alignItems: 'center',
            }}
          >
            {(
              [
                { name: 'is_intermediary',   label: 'Intermediary' },
                { name: 'is_issued',         label: 'Issued' },
                { name: 'is_sent_mail',      label: 'Sent mail' },
                { name: 'is_received_mail', label: 'Received mail' },
                { name: 'is_sent_paper',     label: 'Sent paper' },
                { name: 'is_received_paper', label: 'Received paper' },
              ] as const
            ).map((f) => (
              <Controller key={f.name} control={control} name={f.name}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} size={SM} />}
                    label={f.label}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' } }} />
                )} />
            ))}
          </Box>

          {/* R20: Shipper Code | Shipper Name (readonly, span 2) — always visible per Access */}
          <Controller control={control} name="shipper_code" render={({ field }) => {
            const sel = vendors.find((v) => v.code === field.value)
              ?? (field.value ? { code: field.value, name: '', short_name: null, vat_code: null } : null)
            return (
              <Autocomplete size={SM} options={vendors} value={sel}
                onChange={(_e, v) => field.onChange(v?.code ?? '')}
                getOptionLabel={(o) => o ? `${o.code} — ${o.name ?? ''}` : ''}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                renderInput={(params) => (
                  <TextField {...params} label="Shipper Code"
                    error={!!errors.shipper_code} helperText={errors.shipper_code?.message} />
                )} />
            )
          }} />
          <TextField label="Shipper Name" size={SM} value={shipperName} inputProps={RO} sx={{ ...SPAN2, ...readonlyFieldSx }} />

          {/* R21: Contract Ref | Outright Price (intermediary) | PTBF Diffs (intermediary) */}
          <Controller control={control} name="contract_shipper_intermediary" render={({ field }) => (
            <TextField {...field} label="Contract Ref" size={SM}
              error={!!errors.contract_shipper_intermediary} helperText={errors.contract_shipper_intermediary?.message} />
          )} />
          <Controller control={control} name="outright_shipper_intermediary" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="Outright Price" type="number" size={SM}
              error={!!errors.outright_shipper_intermediary} helperText={errors.outright_shipper_intermediary?.message} />
          )} />
          <Controller control={control} name="ptbfdiffs_shipper_intermediary" render={({ field }) => (
            <TextField {...field} value={field.value === '' ? '' : field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              label="PTBF Diffs" type="number" size={SM}
              error={!!errors.ptbfdiffs_shipper_intermediary} helperText={errors.ptbfdiffs_shipper_intermediary?.message} />
          )} />

        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default LocalPFormDialog
